import { useState } from 'react'
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { useRemoveUserFromOrgMutation } from '../apis/org-admin.api'
import { RemovalModal } from './RemovalModal'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch } from '@/store/store-hooks'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackOrganizationEvent } from '@/analytics/utils/organization-analytics'
import { MattersActions } from '@/matter/store/slice'

interface RemovalDialogContainerProps {
  /** DB user id used for the removal mutation. */
  userId: string
  userEmail: string
  organizationId: string
  orgName: string
  /** Refreshes the roster after a successful removal. */
  refreshData: () => void
  onClose: () => void
  /** Raise the modal above another open modal (e.g. the member roster). Default: false. */
  stacked?: boolean
}

const ALREADY_GONE = 'This member may have already been removed. Refresh and try again.'
const GENERIC_ERROR = 'There was an error removing this member. Please try again.'

/**
 * Maps a removal failure to a user-facing message. Reads the RTK Query error status, which is either
 * an HTTP status number or a string tag (e.g. `'FETCH_ERROR'`) for transport-level failures.
 */
function mapRemovalError(err: unknown): string {
  const error = err as FetchBaseQueryError
  const status = error.status
  const detail = (error.data as { detail?: unknown } | undefined)?.detail

  if (status === 400) {
    const detailStr = typeof detail === 'string' ? detail : ''
    if (detailStr.includes('User is not in an organization') || detailStr.includes('already removed')) {
      return ALREADY_GONE
    }
    if (detailStr.includes('Cannot remove the billing admin')) {
      return "The billing admin can't be removed from the organization."
    }
    return GENERIC_ERROR
  }
  if (status === 403) {
    return "This member can't be removed."
  }
  if (status === 404) {
    return ALREADY_GONE
  }
  if (status === 409) {
    const detailObj = typeof detail === 'object' && detail !== null ? (detail as Record<string, unknown>) : null
    if (detailObj?.['error'] === 'MATTER_NAME_CONFLICT') {
      return 'Removal failed: you have a matter with the same name as one belonging to this member. Rename it and try again.'
    }
    return GENERIC_ERROR
  }
  if (status === 429) {
    return 'Please wait a moment before trying again.'
  }
  if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
    return 'Network error. Check your connection and try again.'
  }
  return GENERIC_ERROR
}

/**
 * Owns the remove-from-organization flow: runs the mutation, maps errors, and on success fires the
 * success toast, tracks analytics, refreshes the roster, and closes the modal. Presentation lives in
 * {@link ./RemovalModal}.
 */
export function RemovalDialogContainer({ userId, userEmail, organizationId, orgName, refreshData, onClose, stacked = false }: RemovalDialogContainerProps) {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const [removeUserFromOrg] = useRemoveUserFromOrgMutation()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleConfirm() {
    setLoading(true)
    setError(null)
    try {
      await removeUserFromOrg(userId).unwrap()
      trackOrganizationEvent(trackEvent, AnalyticsEvent.OrganizationUserRemoved, {
        organizationId,
        removedUserId: userId,
      })
      dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `User removed from ${orgName}`, durationMs: kToastDurationMediumMs }))
      refreshData()
      dispatch(MattersActions.refreshMatterList({ forceRefresh: true }))
      onClose()
    } catch (err) {
      // Keep the modal open so the user can retry or cancel; reset loading so the spinner never locks.
      const message = mapRemovalError(err)
      setError(message)
      setLoading(false)
      // Member was already gone — reconcile the roster so the stale entry disappears.
      // 403 also indicates the target left the org before the guard ran (concurrent removal).
      const status = (err as FetchBaseQueryError).status
      if (message === ALREADY_GONE || status === 403) {
        refreshData()
      }
    }
  }

  return <RemovalModal memberEmail={userEmail} loading={loading} error={error} onConfirm={handleConfirm} onClose={onClose} stacked={stacked} />
}
